<template>
  <div class="d-flex fill-height overflow-hidden position-relative">
    <!-- Left Panel: User List -->
    <v-card
      class="elevation-1 d-flex flex-column"
      :style="{
        width: sidebarCollapsed ? '48px' : '300px',
        overflow: 'hidden',
        transition: 'width 0.22s ease',
        minWidth: 0
      }"
      tile
    >
      <!-- Mini collapsed strip (shown when collapsed) -->
      <div
        v-if="sidebarCollapsed"
        class="d-flex flex-column align-center justify-start flex-grow-1 pt-3 cursor-pointer"
        @click="sidebarCollapsed = false"
        style="overflow: hidden"
      >
        <v-icon size="22" color="grey lighten-1" class="mb-2">mdi-account-group-outline</v-icon>
        <div
          style="
            writing-mode: vertical-rl;
            text-orientation: mixed;
            transform: rotate(180deg);
            font-size: 10px;
            font-weight: 700;
            color: #475569;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            white-space: nowrap;
          "
        >
          Team Members
        </div>
      </div>

      <!-- Full sidebar content (hidden when collapsed) -->
      <template v-if="!sidebarCollapsed">
        <div class="subtitle-1 font-weight-bold pa-4 pb-2 d-flex justify-space-between align-center">
          <span>Team Members</span>
          <div v-if="totalPages > 1">
            <v-btn
              icon
              small
              @click="$emit('page-change', currentPage - 1)"
              :disabled="currentPage <= 1"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="caption">{{ currentPage }} / {{ totalPages }}</span>
            <v-btn
              icon
              small
              @click="$emit('page-change', currentPage + 1)"
              :disabled="currentPage >= totalPages"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
        <v-divider></v-divider>
        <div class="pa-2">
          <v-autocomplete
            v-if="roles.length > 0"
            v-model="localRole"
            :items="roles"
            dense
            outlined
            placeholder="Filter by Role"
            hide-details
            clearable
            class="mb-2"
          ></v-autocomplete>
          <v-text-field
            v-model="localSearch"
            dense
            outlined
            placeholder="Search employee..."
            prepend-inner-icon="mdi-magnify"
            clearable
            @input="handleSearch"
          ></v-text-field>
        </div>
        <v-list dense class="overflow-y-auto flex-grow-1" style="max-height: 100%">
          <v-list-item-group v-model="selectedUserIndex" color="primary" mandatory>
            <v-list-item
              v-for="user in filteredUsers"
              :key="user._id"
              @click="$emit('select-user', user)"
              style="min-height: 60px"
            >
              <v-list-item-content>
                <EmployeeInfo :item="user" :show-owner-tag="false" show-info-icon />
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </template>
    </v-card>

    <!-- Collapse toggle button overlaying the border -->
    <div
      style="
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 5;
        pointer-events: none;
        display: flex;
        align-items: center;
      "
      :style="{ left: sidebarCollapsed ? '48px' : '300px', transition: 'left 0.22s ease' }"
    >
      <button
        @click="sidebarCollapsed = !sidebarCollapsed"
        style="
          pointer-events: all;
          width: 18px;
          height: 48px;
          background: #cbd5e1;
          border: 1px solid #94a3b8;
          border-left: none;
          border-radius: 0 6px 6px 0;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 2px 0 6px rgba(0, 0, 0, 0.12);
          padding: 0;
          outline: none;
          transition: background 0.15s;
        "
        @mouseover="$event.currentTarget.style.background = '#94a3b8'"
        @mouseleave="$event.currentTarget.style.background = '#cbd5e1'"
      >
        <v-icon size="13" color="grey darken-3">
          {{ sidebarCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left' }}
        </v-icon>
      </button>
    </div>

    <!-- Right Content Area (slot for Map / Calendar) -->
    <div class="flex-grow-1 d-flex flex-column overflow-hidden">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import EmployeeInfo from '@/components/EmployeeInfo.vue'
import { debounce } from '@/utils/common'

export default {
  name: 'TeamReviewSidebar',
  components: { EmployeeInfo },
  props: {
    employees: { type: Array, default: () => [] },
    currentPage: { type: Number, default: 1 },
    totalPages: { type: Number, default: 1 },
    selectedUserId: { type: String, default: null },
    search: { type: String, default: '' },
    selectedRole: { type: String, default: null }
  },
  data() {
    return {
      localSearch: this.search || '',
      localRole: this.selectedRole || null,
      sidebarCollapsed: false
    }
  },
  computed: {
    roles() {
      const rolesSet = new Set(this.employees.map((u) => u?.wfmRole?.role).filter((r) => r))
      return Array.from(rolesSet).sort()
    },
    filteredUsers() {
      let users = this.employees

      if (this.localRole) {
        users = users.filter((u) => u.wfmRole?.role === this.localRole)
      }

      if (!this.localSearch) return users

      const s = this.localSearch.toLowerCase()
      return users.filter((u) => {
        const name = u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim()
        return name.toLowerCase().includes(s)
      })
    },
    selectedUserIndex() {
      if (!this.selectedUserId) return 0
      const idx = this.filteredUsers.findIndex((u) => u._id === this.selectedUserId)
      return idx >= 0 ? idx : 0
    }
  },
  watch: {
    search(val) {
      this.localSearch = val
    },
    selectedRole(val) {
      this.localRole = val
    },
    localRole(val) {
      this.$emit('update:selectedRole', val)
    }
  },
  methods: {
    handleSearch: debounce(function (val) {
      this.$emit('update:search', val || '')
    }, 300)
  }
}
</script>

<style scoped>
/* match original sidebar styles */
</style>
